import { Component, forwardRef, Input, OnInit, ViewChild } from '@angular/core';
import { ControlContainer, ControlValueAccessor, NgForm, NgModelGroup, NG_VALUE_ACCESSOR } from '@angular/forms';

import { AccessControl } from 'rev-shared/media/MediaConstants';
import { IInsightRecord } from 'rev-shared/ui/insight/ngx/Insight.Contract';
import { InsightSearchHelper } from 'rev-portal/search/InsightSearchHelper.Service';
import { MediaFeaturesService } from 'rev-shared/media/MediaFeatures.Service';
import { SecurityContextService } from 'rev-shared/security/SecurityContext.Service';
import { UserAuthorizationService } from 'rev-shared/security/UserAuthorization.Service';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { combineErrors } from 'rev-shared/util/directives/form/validation/validationUtil';

import { ITeam, IAccessControlModel, IAccessControlEntity } from './VideoSettings.Contract';
import styles from './video-access-control.module.less';

@Component({
	selector: 'video-access-control-input',
	templateUrl: './VideoAccessControlAngular.Component.html',
	providers: [
		{
		  provide: NG_VALUE_ACCESSOR,
		  multi: true,
		  useExisting: forwardRef(() => VideoAccessControlAngularComponent),
		}
	],
	viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})
export class VideoAccessControlAngularComponent implements ControlValueAccessor, OnInit {
	@Input() public fetchAssignedEntityDetails: boolean;
	@Input() public showAllChannels: boolean;
	@Input() public hideLabel: boolean;
	@Input() public teams: ITeam[];
	@Input() public videoId: string;

	@Input() public model: IAccessControlModel;

	@ViewChild('videoAccessControlGroup') public modelForm: NgModelGroup;

	public onTouched: () => void;
	public onChange: (model: IAccessControlModel) => void;

	public readonly AccessControl = AccessControl;
	public readonly styles = styles;
	public accessControlInsightOptions: any;
	public initialACLEntities: IAccessControlEntity[];
	public initialAccessControl: AccessControl;
	public isPublic: boolean;
	public isAllInternalUsers: boolean;
	public ready: boolean;

	public hasContentCreatorRights: boolean;
	public hasSetVideoPublicRights: boolean;

	constructor(
		private UserContext: UserContextService,
		private InsightSearchHelper: InsightSearchHelper,
		public MediaFeatures: MediaFeaturesService,
		UserAuthorization: UserAuthorizationService,
		SecurityContext: SecurityContextService
	) {
		this.hasContentCreatorRights = UserAuthorization.hasContentCreatorRights();
		this.hasSetVideoPublicRights = SecurityContext.checkAuthorization('media.setVideoPublic');
	}

	public writeValue(obj: any): void {
		if (obj) {
			this.model = { ...obj };
			this.setupControls();
		}
	}

	public ngOnInit(): void {
		setTimeout(() => {
			this.ready = true;
			this.validatePublicAccess();
		}, 500);
	}

	public registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	public registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	public setDisabledState?(isDisabled: boolean): void {
		//throw new Error('Method not implemented.');
	}

	public onPublicChange(): void {
		this.isAllInternalUsers = this.isPublic || this.isAllInternalUsers;
		this.onAccessControlChange();
	}

	public onAllInternalChange(): void {
		this.onAccessControlChange();
	}

	public onAccessControlChange(): void {
		this.validatePublicAccess();
		this.model.teams = [];
		this.onChangeAcl();
	}

	public onChangeAcl(): void {
		this.addEditAccessToAddedEntities(this.model.accessControlEntities);

		this.model = <IAccessControlModel>{
			accessControl: this.accessControl,
			accessControlEntities: this.model.accessControlEntities,
			password: this.model.password,
			teams: this.model.teams
		};
		this.onChange(this.model);
	}

	public get allUsersTooltip(): string {
		return !this.MediaFeatures.accountFeatures.enablePublicVideoAccess && this.isPublic ? 'Media_Videos_PublicVideoValidation' : '';
	}

	public addEditAccessToAddedEntities(modifiedEntityList: IAccessControlEntity[]): void {
		if (this.isForceEditAccess) {
			const addedEntities = modifiedEntityList.filter(({ id: id1 }) => !this.initialACLEntities.some(({ id: id2 }) => id2 === id1));
			for (const entity of addedEntities) {
				entity.canEdit = true;
			}
		}
	}

	public get isPublicToggleVisible(): boolean {
		return this.hasSetVideoPublicRights && this.MediaFeatures.accountFeatures.enablePublicVideoAccess
			|| this.initialAccessControl === AccessControl.Public
			|| this.modelForm?.control?.controls?.accessControlAllUsers.errors?.isProhibitedPublicAccess;
	}

	private setupControls() : void {
		this.initialAccessControl = this.model.accessControl;
		this.initialACLEntities = this.model.accessControlEntities.map(a => ({ ...a }));
		this.updateAccessControl();
		this.validatePublicAccess();

		this.accessControlInsightOptions = this.InsightSearchHelper.buildAccessEntityInsightOptions({
			accountId: this.UserContext.getAccount().id,
			users: this.hasContentCreatorRights,
			groups: this.hasContentCreatorRights,
			teams: true,
			showEditAccessInput: (item: IInsightRecord) => item.type !== 'Team',
			disableAssignedInfiniteScroll: !this.fetchAssignedEntityDetails,
			showAllChannels: this.showAllChannels,
			getEntityIds: this.fetchAssignedEntityDetails
				? () => (this.model.accessControlEntities || []).map(e => e.id)
				: undefined
		});

		if (!this.fetchAssignedEntityDetails) {
			//data must be populated if not using infinite scroll.
			const properties: any = {
				data: {
					get: () => this.model.accessControlEntities,
					set: value => this.model.accessControlEntities = value
				}
			};
			Object.defineProperties(this.accessControlInsightOptions, properties);
		}
	}

	private validatePublicAccess(): void {
		const invalid = this.isPublic && !this.MediaFeatures.accountFeatures.enablePublicVideoAccess;
		combineErrors(this.modelForm?.control, { 'isProhibitedPublicAccess': invalid });
	}

	private updateAccessControl(): void {
		this.isPublic = this.model.accessControl === AccessControl.Public;
		this.isAllInternalUsers = this.isPublic || this.model.accessControl === AccessControl.AllUsers;
	}

	private get accessControl(): AccessControl {
		return this.isPublic ? AccessControl.Public
			: this.isAllInternalUsers ? AccessControl.AllUsers
				: AccessControl.Private;
	}

	private get isForceEditAccess(): boolean {
		return this.accessControl !== AccessControl.Private;
	}
}
